import type {
  BookingSkinGlow,
  BookingSkinMotion,
} from "./bookingVisualVariants";

/** The class `EditorialBookingPageRoot` adds when its skin has an ambient layer. */
export const BOOKING_AMBIENT_CLASS = "openings-booking-ambient";

const SCOPES = [".openings-editorial-booking-theme", ".openings-booking-portal"];

/** `selector` under the booking root and under its portaled overlays. */
function scoped(...selectors: string[]): string {
  return SCOPES.flatMap((scope) =>
    selectors.map((selector) => `${scope} ${selector}`),
  ).join(",\n  ");
}

function rgbaOf(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** The ambient layer's CSS `background`: one radial glow per light. */
export function ambientBackground(glows: readonly BookingSkinGlow[]): string {
  return glows
    .map(
      ({ color, opacity, at, size }) =>
        `radial-gradient(ellipse ${size} at ${at}, ${rgbaOf(
          color,
          opacity,
        )} 0%, ${rgbaOf(color, 0)} 70%)`,
    )
    .join(", ");
}

/**
 * The CSS for a skin's motion, or `""` when it has none, so every preset
 * without motion emits nothing new. Only `transform` animates; colors keep
 * the transitions the components already had.
 */
export function buildSkinMotionCss(motion?: BookingSkinMotion): string {
  if (!motion) return "";
  const blocks: string[] = [];

  if (motion.ambient) {
    // The layer sits in the root's own stacking context (isolation), below
    // its content and above its canvas background. Fixed, so it stays put
    // while the page scrolls; oversized, so the drift never shows an edge.
    blocks.push(`
  .${BOOKING_AMBIENT_CLASS} {
    isolation: isolate;
  }

  .${BOOKING_AMBIENT_CLASS}::before {
    content: "";
    position: fixed;
    inset: -20%;
    z-index: -1;
    pointer-events: none;
    background: ${ambientBackground(motion.ambient.glows)};
    animation: openings-booking-ambient-drift ${motion.ambient.seconds}s ease-in-out infinite alternate;
    will-change: transform;
  }

  @keyframes openings-booking-ambient-drift {
    0% { transform: translate3d(-4%, -3%, 0) rotate(0deg) scale(1); }
    50% { transform: translate3d(3%, 2%, 0) rotate(6deg) scale(1.08); }
    100% { transform: translate3d(5%, -2%, 0) rotate(-4deg) scale(1.03); }
  }`);
  }

  if (motion.press) {
    // TimeButton sets its color transitions inline; restate them and add
    // transform and shadow. Ant buttons already transition every property.
    // Hover only where a pointer hovers: on touch, a tapped slot would keep
    // its hover lift. Hover excludes :active, whose selector is less
    // specific, so a held-down slot presses in instead of staying lifted.
    blocks.push(`
  ${scoped(".openings-time-btn")} {
    transition: background 0.15s ease, border-color 0.15s ease,
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 180ms ease !important;
  }

  @media (hover: hover) {
    ${scoped(
      '.openings-time-btn:hover:not([aria-pressed="true"]):not(:active)',
      ".ant-btn-primary:not(:disabled):hover:not(:active)",
    )} {
      transform: ${motion.press.hoverTransform};
      box-shadow: ${motion.press.hoverShadow};
    }
  }

  ${scoped(".openings-time-btn:active", ".ant-btn-primary:not(:disabled):active")} {
    transform: scale(0.97);
    box-shadow: none;
  }

  ${scoped('.openings-time-btn[aria-pressed="true"]')} {
    animation: openings-booking-slot-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes openings-booking-slot-pop {
    0% { transform: scale(0.92); }
    100% { transform: scale(1); }
  }`);
  }

  blocks.push(`
  @media (prefers-reduced-motion: reduce) {
    .${BOOKING_AMBIENT_CLASS}::before {
      animation: none;
    }

    ${scoped(".openings-time-btn", ".ant-btn-primary")} {
      animation: none !important;
      transform: none !important;
    }
  }`);

  return blocks.join("\n");
}
